import {
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  List,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import TimelineSegmentActions, {
  sortTimelineRows,
} from "./TimelineSegmentActions";
import {
  camadaAReverter,
  categoriaPrePreenchida,
  corDaOrigem,
  escopoClienteDisponivel,
  linhaBaseDaTimeline,
  nivelDaOrigem,
  resumoImpactoOuran,
  rotuloDaOrigem,
  temCamadaParaReverter,
  vinculoEstaDesviando,
} from "./ouranSkuView";

const { Text } = Typography;
const formatMoney = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
// O DRF devolve erro de campo como {campo: ["mensagem"]}. Sem varrer isso, uma
// validação específica (ex.: categoria de filial em ajuste de cliente) chegava ao
// usuário como "não foi possível", sem dizer o motivo.
const mensagemDeErro = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (!data) return fallback;
  if (data.detail) return data.detail;
  const primeiro = Object.values(data).find(
    (v) => typeof v === "string" || (Array.isArray(v) && v.length)
  );
  if (typeof primeiro === "string") return primeiro;
  if (Array.isArray(primeiro) && typeof primeiro[0] === "string")
    return primeiro[0];
  return fallback;
};
const TAMANHO_PAGINA = 50;
const hintStyle = {
  fontSize: 12,
  color: "rgba(0, 0, 0, 0.45)",
  marginLeft: 24,
};

export default function ManualSkuAssignment() {
  const { user } = useAuth();
  const [messageApi, contextHolder] = message.useMessage();
  const {
    getManualProductAssignments,
    saveManualProductAssignments,
    removeManualProductAssignment,
    addManualCollection,
    getSkuCollectionHistory,
    patchSkuCollectionSegment,
    deleteSkuCollectionSegment,
    getOuranCatalogPilot,
    getOuranCatalogSkus,
    getOuranCatalogSkuFiltros,
    createOuranCatalogCategory,
    editOuranCatalogOverride,
    deleteOuranCatalogOverride,
    previewOuranCatalogOverride,
    saveOuranCatalogOverride,
    getOuranCatalogOverrideHistory,
    getOuranCatalogModePreview,
    activateOuranCatalogMode,
  } = useApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterCollection, setFilterCollection] = useState(null);
  // Modo Ouran: categoria efetiva (id ou "__none__") e categoria ERP
  // (col_sequencial ou "__none__"), filtradas no servidor antes de paginar.
  const [filterCategoria, setFilterCategoria] = useState(null);
  const [filterCategoriaErp, setFilterCategoriaErp] = useState(null);
  const [erpFilterOptions, setErpFilterOptions] = useState([]);
  // pending: { [produto]: col_sequencial }
  const [pendingChanges, setPendingChanges] = useState({});
  const [newCollectionName, setNewCollectionName] = useState("");
  const [addingCollection, setAddingCollection] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkCollection, setBulkCollection] = useState(null);
  const [historyModalProduto, setHistoryModalProduto] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [catalogPilot, setCatalogPilot] = useState(null);
  const [showOuranPilot, setShowOuranPilot] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [totalProdutos, setTotalProdutos] = useState(0);
  const [periodo, setPeriodo] = useState(null);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryForm] = Form.useForm();
  const [adjustmentForm] = Form.useForm();
  const adjustmentIntencao = Form.useWatch("intencao", adjustmentForm);
  const adjustmentScope = Form.useWatch("scope", adjustmentForm);
  // Categoria de filial não vale para todas as filiais: em ajuste de cliente ela
  // nem deve ser oferecida, senão o usuário só descobre no erro do salvamento.
  const categoriasDisponiveis = (catalogPilot?.categories || []).filter(
    (categoria) => adjustmentScope !== "client" || categoria.escopo !== "branch"
  );
  const precisaCategoria = ["exposicao", "correcao"].includes(
    adjustmentIntencao
  );
  const [ouranAdjustment, setOuranAdjustment] = useState(null);
  const [ouranAdjustmentPreview, setOuranAdjustmentPreview] = useState(null);
  const [ouranAdjustmentPayload, setOuranAdjustmentPayload] = useState(null);
  const [ouranAdjustmentSaving, setOuranAdjustmentSaving] = useState(false);
  // Sem vínculo nem correção ativos o SKU já está na padrão: "voltar" não teria
  // o que encerrar. Precisa vir depois de ouranAdjustment: const não é içado
  // com valor, e ler antes da declaração derruba o componente.
  const temAjusteAtivo = temCamadaParaReverter(ouranAdjustment?.ouran_pilot);
  // O EAN vem do cadastro, que é a mesma fonte que o backend usa para decidir
  // se aceita o ajuste por cliente. Olhar outro campo aqui oferecia o que o
  // salvamento recusaria, ou escondia o que ele aceitaria.
  const escopoCliente = escopoClienteDisponivel(
    ouranAdjustment?.ouran_pilot,
    catalogPilot?.can_adjust_client
  );
  const [excluindo, setExcluindo] = useState(null);
  const [edicao, setEdicao] = useState(null);
  const [edicaoSaving, setEdicaoSaving] = useState(false);
  const [edicaoForm] = Form.useForm();
  const edicaoResolution = Form.useWatch("resolution", edicaoForm);
  const [ouranHistoryProduct, setOuranHistoryProduct] = useState(null);
  const [ouranHistoryRows, setOuranHistoryRows] = useState([]);
  const [ouranHistoryLoading, setOuranHistoryLoading] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [activatePreview, setActivatePreview] = useState(null);
  const [activatePreviewLoading, setActivatePreviewLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateForm] = Form.useForm();
  // Mirrors historyModalProduto so async loadHistory responses can tell,
  // after the fact, whether they're still for the modal's current produto.
  const historyModalProdutoRef = useRef(null);

  const activeBranchId = user?.active_branch_id;
  const activeBranchRef = useRef(activeBranchId);
  activeBranchRef.current = activeBranchId;
  const productsRequestRef = useRef(0);
  const [readyBranch, setReadyBranch] = useState(null);
  const filtersKey = JSON.stringify([
    searchText.trim(),
    filterCollection,
    filterCategoria,
    filterCategoriaErp,
    periodo?.[0]?.format("YYYY-MM-DD"),
    periodo?.[1]?.format("YYYY-MM-DD"),
  ]);
  const filtersRef = useRef(filtersKey);
  filtersRef.current = filtersKey;

  useEffect(() => {
    // col_sequencial é um ID de coleção escopado à filial anterior — sem
    // isto ele sobrevive à troca de filial e paginaParams() reenvia o
    // filtro errado para a filial nova (achado pela revisão do Codex).
    setFilterCollection(null);
    setFilterCategoria(null);
    setFilterCategoriaErp(null);
    loadData();
    return () => {
      productsRequestRef.current += 1;
    };
    // The active branch changes both the SKU namespace and pilot snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  // No modo Ouran a lista é o lote (100-300 SKUs) e já vem pronta do piloto;
  // buscar os 8.131 SKUs da filial só para achar esses era o que travava a tela.
  // No modo atual a lista vem paginada do servidor.
  const loadData = async () => {
    const requestId = ++productsRequestRef.current;
    const isCurrent = () =>
      productsRequestRef.current === requestId &&
      activeBranchRef.current === activeBranchId;
    setReadyBranch(null);
    setLoading(true);
    try {
      // O modo Ouran deixou de depender de lote: se a filial tem catálogo, ela
      // lista os SKUs vendidos no período, paginados pelo servidor, como o
      // modo atual já fazia. `pilot` serve só para saber se o modo existe e
      // trazer categorias e permissões.
      const pilot = await getOuranCatalogPilot().catch(() => null);
      if (!isCurrent()) return;
      // A tela segue o modo real da filial (decisão 2.3) — não existe mais
      // alternância manual de preview. Uma filial em modo ERP nunca mostra a
      // tela nova, mesmo que o piloto Ouran responda (ex.: catálogo global
      // existe mas esta filial ainda não migrou).
      const temOuran = pilot?.category_mode === "ouran";
      setCatalogPilot(pilot || null);
      setShowOuranPilot(temOuran);
      const data = await getManualProductAssignments(
        temOuran ? { page_size: 1 } : paginaParams(1)
      );
      if (!isCurrent()) return;
      const sorted = [...(data.manual_collections || [])].sort((a, b) =>
        (a.label || "").localeCompare(b.label || "", "pt-BR")
      );
      setCollectionOptions(sorted);
      setPendingChanges({});
      if (temOuran) {
        const lista = await getOuranCatalogSkus(paginaParams(1));
        if (!isCurrent()) return;
        aplicarListaOuran(lista, 1);
      } else if (filtersRef.current === filtersKey) {
        aplicarPagina(data, 1);
      }
      setReadyBranch(activeBranchId);
    } catch {
      if (isCurrent()) messageApi.error("Erro ao carregar produtos.");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const aplicarListaOuran = (data, page) => {
    const rows = data?.products || [];
    setProducts(
      rows.map((row) => ({
        produto: row.produto,
        descricao: row.descricao,
        ean: row.ean,
        col_sequencial: null,
        ouran_pilot: row,
      }))
    );
    setTotalProdutos(data?.count ?? rows.length);
    setPagina(page);
  };

  const aplicarPagina = (data, page) => {
    setProducts(
      (data.products || []).map((row) => ({ ...row, ouran_pilot: null }))
    );
    setTotalProdutos(data.count ?? (data.products || []).length);
    setPagina(page);
  };

  const periodoParams = () => ({
    ...(periodo?.[0] ? { start_date: periodo[0].format("YYYY-MM-DD") } : {}),
    ...(periodo?.[1] ? { end_date: periodo[1].format("YYYY-MM-DD") } : {}),
  });

  const paginaParams = (page) => ({
    page,
    page_size: TAMANHO_PAGINA,
    ...(searchText.trim() ? { search: searchText.trim() } : {}),
    ...(filterCollection != null ? { col_sequencial: filterCollection } : {}),
    ...(filterCategoria != null ? { categoria: filterCategoria } : {}),
    ...(filterCategoriaErp != null
      ? { categoria_erp: filterCategoriaErp }
      : {}),
    ...periodoParams(),
  });

  const carregarPagina = async (page) => {
    const requestId = ++productsRequestRef.current;
    const isCurrent = () =>
      productsRequestRef.current === requestId &&
      activeBranchRef.current === activeBranchId &&
      filtersRef.current === filtersKey;
    setLoading(true);
    try {
      if (showOuranPilot) {
        const lista = await getOuranCatalogSkus(paginaParams(page));
        if (!isCurrent()) return;
        aplicarListaOuran(lista, page);
        return;
      }
      const data = await getManualProductAssignments(paginaParams(page));
      if (!isCurrent()) return;
      aplicarPagina(data, page);
    } catch {
      if (isCurrent()) messageApi.error("Erro ao carregar produtos.");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Busca, categoria e período são do servidor nos dois modos. Debounce para
  // não disparar uma consulta a cada tecla.
  useEffect(() => {
    // Espera apenas a identificação do modo da filial. Um request de página
    // em andamento não pode impedir que os filtros mais recentes sejam buscados.
    if (readyBranch !== activeBranchId) return undefined;
    const t = setTimeout(() => carregarPagina(1), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, showOuranPilot, readyBranch, activeBranchId]);

  // Opções do filtro "Categoria ERP": só as que venderam no período.
  const periodoKey = JSON.stringify(periodoParams());
  useEffect(() => {
    if (!showOuranPilot || readyBranch !== activeBranchId) {
      setErpFilterOptions([]);
      return undefined;
    }
    let vigente = true;
    getOuranCatalogSkuFiltros(periodoParams())
      .then((data) => {
        if (!vigente) return;
        const semErp = data?.skus_sem_categoria_erp || 0;
        setErpFilterOptions([
          ...(semErp
            ? [
                {
                  value: "__none__",
                  label: `— Sem categoria ERP — (${semErp})`,
                },
              ]
            : []),
          ...(data?.erp_categories || []).map((item) => ({
            value: item.col_sequencial,
            label: `${item.name} (${item.skus})`,
          })),
        ]);
      })
      .catch(() => {
        if (vigente) setErpFilterOptions([]);
      });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoKey, showOuranPilot, readyBranch, activeBranchId]);

  // Returns the freshly-sorted rows on success so callers (e.g. the
  // history modal's onDone) can also sync the main grid — returns
  // undefined if the response is stale or errored.
  const loadHistory = async (produto) => {
    setHistoryLoading(true);
    try {
      const rows = await getSkuCollectionHistory(produto);
      // Discard this response if the modal has since moved on to a
      // different produto (or was closed) while the request was in flight.
      if (historyModalProdutoRef.current !== produto) return undefined;
      const sorted = sortTimelineRows(rows);
      setHistoryRows(sorted);
      return sorted;
    } catch {
      if (historyModalProdutoRef.current !== produto) return undefined;
      messageApi.error("Erro ao carregar histórico.");
      setHistoryRows([]);
      return undefined;
    } finally {
      if (historyModalProdutoRef.current === produto) {
        setHistoryLoading(false);
      }
    }
  };

  // Refreshes one produto's history AND, if the mutation that just
  // happened touched its currently-active segment, syncs the main grid's
  // "Categoria manual" value so it doesn't go stale until a full reload.
  const refreshHistoryAndGridRow = async (produto) => {
    const rows = await loadHistory(produto);
    if (!rows) return;
    const activeRow = rows.find((r) => !r.union_end_datetime);
    setProducts((prev) =>
      prev.map((p) =>
        p.produto === produto
          ? {
              ...p,
              col_sequencial: activeRow ? activeRow.col_sequencial : null,
            }
          : p
      )
    );
  };

  const openHistoryModal = (produto) => {
    historyModalProdutoRef.current = produto;
    setHistoryRows([]);
    setHistoryModalProduto(produto);
    loadHistory(produto);
  };

  const handleCollectionChange = (produto, colSequencial) => {
    if (colSequencial == null) {
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[produto];
        return next;
      });
    } else {
      setPendingChanges((prev) => ({ ...prev, [produto]: colSequencial }));
    }
  };

  const handleSave = async () => {
    const assignments = Object.entries(pendingChanges).map(
      ([produto, col_sequencial]) => ({ produto, col_sequencial })
    );
    if (!assignments.length) {
      messageApi.info("Nenhuma alteração pendente.");
      return;
    }
    setSaving(true);
    try {
      const result = await saveManualProductAssignments(assignments);
      messageApi.success(`${result.updated} produto(s) atualizado(s).`);
      // Apply saved changes to local products state — no full reload
      setProducts((prev) =>
        prev.map((p) =>
          p.produto in pendingChanges
            ? { ...p, col_sequencial: pendingChanges[p.produto] }
            : p
        )
      );
      setPendingChanges({});
    } catch {
      messageApi.error("Erro ao salvar atribuições.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (produto) => {
    try {
      await removeManualProductAssignment(produto);
      // Update local state immediately — no full reload
      setProducts((prev) =>
        prev.map((p) =>
          p.produto === produto ? { ...p, col_sequencial: null } : p
        )
      );
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[produto];
        return next;
      });
    } catch {
      messageApi.error("Erro ao remover atribuição.");
    }
  };

  const handleAddCollection = async () => {
    const name = newCollectionName.trim();
    if (!name) return;
    setAddingCollection(true);
    try {
      const col = await addManualCollection(name);
      setCollectionOptions((prev) =>
        [...prev, col].sort((a, b) =>
          (a.label || "").localeCompare(b.label || "", "pt-BR")
        )
      );
      setNewCollectionName("");
      messageApi.success(`Categoria "${col.label}" criada.`);
    } catch {
      messageApi.error("Erro ao criar categoria.");
    } finally {
      setAddingCollection(false);
    }
  };

  const abrirAtivacaoOuran = async () => {
    setActivateOpen(true);
    setActivatePreview(null);
    activateForm.resetFields();
    setActivatePreviewLoading(true);
    try {
      const preview = await getOuranCatalogModePreview();
      setActivatePreview(preview);
      const maisRecente = preview.releases?.[0];
      activateForm.setFieldsValue({
        release_id: maisRecente?.id,
        reason: "",
      });
    } catch (error) {
      messageApi.error(
        mensagemDeErro(error, "Não foi possível carregar a prévia.")
      );
      setActivateOpen(false);
    } finally {
      setActivatePreviewLoading(false);
    }
  };

  const confirmarAtivacaoOuran = async (values) => {
    setActivating(true);
    try {
      await activateOuranCatalogMode({
        mode: "ouran",
        release_id: values.release_id,
        reason: values.reason.trim(),
      });
      messageApi.success("Filial ativada em modo Ouran.");
      setActivateOpen(false);
      await loadData();
    } catch (error) {
      messageApi.error(
        mensagemDeErro(error, "Não foi possível ativar o modo Ouran.")
      );
    } finally {
      setActivating(false);
    }
  };

  const handleBulkAssign = () => {
    if (!bulkCollection || !selectedRowKeys.length) return;
    setPendingChanges((prev) => {
      const next = { ...prev };
      for (const produto of selectedRowKeys) {
        next[produto] = bulkCollection;
      }
      return next;
    });
    setSelectedRowKeys([]);
    setBulkCollection(null);
  };

  const openOuranAdjustment = (record) => {
    const pilot = record.ouran_pilot;
    setOuranAdjustment(record);
    setOuranAdjustmentPreview(null);
    setOuranAdjustmentPayload(null);
    adjustmentForm.setFieldsValue({
      scope: "branch",
      category_id: categoriaPrePreenchida(pilot),
      start_date: dayjs(),
      reason: "",
      intencao: "exposicao",
    });
  };

  const handleCreateCategory = async (values) => {
    setCategorySaving(true);
    try {
      const created = await createOuranCatalogCategory({
        name: values.name.trim(),
        escopo: values.escopo,
      });
      messageApi.success(`Categoria "${created.name}" criada.`);
      setCategoryOpen(false);
      categoryForm.resetFields();
      // Insere no estado em vez de recarregar: um loadData aqui puxaria todos
      // os SKUs da filial no meio do fluxo de ajuste.
      setCatalogPilot((prev) =>
        prev
          ? {
              ...prev,
              categories: [...(prev.categories || []), created].sort((a, b) =>
                (a.name || "").localeCompare(b.name || "", "pt-BR")
              ),
            }
          : prev
      );
      adjustmentForm.setFieldsValue({ category_id: created.id });
    } catch (error) {
      messageApi.error(
        mensagemDeErro(error, "Não foi possível criar a categoria.")
      );
    } finally {
      setCategorySaving(false);
    }
  };

  // A tela pergunta a intenção; o modelo guarda dois eixos (tipo e resolution).
  // "Voltar ao padrão" remove uma camada por clique: primeiro o vínculo
  // temporário se estiver desviando, depois a correção de padrão. Um vínculo
  // já esvaziado num clique anterior não é mais uma camada — camadaAReverter
  // ignora esse registro residual e vai direto para a correção.
  const traduzIntencao = (intencao, pilot) => {
    if (intencao === "exposicao")
      return { tipo: "vinculo_local", resolution: "category" };
    if (intencao === "correcao")
      return { tipo: "correcao_padrao", resolution: "category" };
    if (intencao === "sem_categoria")
      return { tipo: "vinculo_local", resolution: "unclassified" };
    return { tipo: camadaAReverter(pilot), resolution: "standard" };
  };

  const buildOuranAdjustmentPayload = (values) => {
    const { tipo, resolution } = traduzIntencao(
      values.intencao,
      ouranAdjustment?.ouran_pilot
    );
    return {
      produto: String(ouranAdjustment.produto),
      scope: values.scope,
      tipo,
      resolution,
      category_id: resolution === "category" ? values.category_id : null,
      start_date: values.start_date.format("YYYY-MM-DD"),
      reason: values.reason.trim(),
    };
  };

  const prepareOuranAdjustment = async () => {
    try {
      const values = await adjustmentForm.validateFields();
      const payload = buildOuranAdjustmentPayload(values);
      setOuranAdjustmentSaving(true);
      const preview = await previewOuranCatalogOverride(payload);
      setOuranAdjustmentPayload(payload);
      setOuranAdjustmentPreview(preview);
    } catch (error) {
      if (error?.errorFields) return;
      messageApi.error(
        mensagemDeErro(error, "Não foi possível calcular o impacto.")
      );
    } finally {
      setOuranAdjustmentSaving(false);
    }
  };

  const confirmOuranAdjustment = async () => {
    if (!ouranAdjustmentPayload) return;
    setOuranAdjustmentSaving(true);
    try {
      const result = await saveOuranCatalogOverride(ouranAdjustmentPayload);
      messageApi.success("Ajuste Ouran registrado na timeline.");
      setOuranAdjustment(null);
      setOuranAdjustmentPreview(null);
      setOuranAdjustmentPayload(null);
      // Só as linhas afetadas voltam recalculadas: recarregar a tela inteira
      // custa milhares de SKUs a cada ajuste.
      const afetadas = new Map(
        (result?.produtos || []).map((row) => [String(row.produto), row])
      );
      if (afetadas.size) {
        setProducts((prev) =>
          prev.map((p) =>
            afetadas.has(String(p.produto))
              ? { ...p, ouran_pilot: afetadas.get(String(p.produto)) }
              : p
          )
        );
      } else {
        await loadData();
      }
    } catch (error) {
      messageApi.error(
        mensagemDeErro(error, "Não foi possível registrar o ajuste.")
      );
    } finally {
      setOuranAdjustmentSaving(false);
    }
  };

  const abrirEdicao = (row) => {
    setEdicao(row);
    edicaoForm.setFieldsValue({
      resolution: row.resolution,
      category_id: row.category_id || null,
      start_date: dayjs(row.start),
      reason: row.reason || "",
    });
  };

  const confirmarExclusao = (row) => {
    // Excluir um ajuste de filial que estava vencendo um ajuste do cliente não
    // deixa o SKU no padrão: o do cliente volta a valer aqui. Sem dizer isso, a
    // exclusão parece não ter funcionado.
    const sobreposto = ouranHistoryRows.find(
      (r) =>
        r.id !== row.id &&
        !r.end &&
        r.tipo === row.tipo &&
        r.scope === "client" &&
        row.scope === "branch"
    );
    Modal.confirm({
      title: "Excluir este ajuste?",
      content: sobreposto
        ? `Este ajuste de filial está vencendo o ajuste de todo o cliente ("${
            sobreposto.category_name || "sem categoria"
          }"). Ao excluir, o ajuste do cliente volta a valer nesta filial.`
        : "O SKU volta ao que valia antes deste ajuste. Use isto para desfazer " +
          "engano — inclusive escopo errado, que não é editável.",
      okText: "Excluir",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setExcluindo(row.id);
        try {
          const r = await deleteOuranCatalogOverride(row.id);
          messageApi.success(
            r?.anterior_reaberto
              ? "Ajuste excluído; o anterior voltou a valer."
              : "Ajuste excluído."
          );
          if (ouranHistoryProduct) await openOuranHistory(ouranHistoryProduct);
          await loadData();
        } catch (error) {
          messageApi.error(
            mensagemDeErro(error, "Não foi possível excluir o ajuste.")
          );
        } finally {
          setExcluindo(null);
        }
      },
    });
  };

  const salvarEdicao = async (values) => {
    setEdicaoSaving(true);
    try {
      await editOuranCatalogOverride(edicao.id, {
        resolution: values.resolution,
        category_id:
          values.resolution === "category" ? values.category_id : null,
        start_date: values.start_date.format("YYYY-MM-DD"),
        reason: values.reason.trim(),
      });
      messageApi.success("Ajuste atualizado.");
      setEdicao(null);
      if (ouranHistoryProduct) await openOuranHistory(ouranHistoryProduct);
      await loadData();
    } catch (error) {
      messageApi.error(
        mensagemDeErro(error, "Não foi possível atualizar o ajuste.")
      );
    } finally {
      setEdicaoSaving(false);
    }
  };

  const openOuranHistory = async (record) => {
    setOuranHistoryProduct(record);
    setOuranHistoryRows([]);
    setOuranHistoryLoading(true);
    try {
      const rows = await getOuranCatalogOverrideHistory({
        produto: String(record.produto),
      });
      setOuranHistoryRows(rows);
    } catch {
      messageApi.error("Não foi possível carregar a timeline Ouran.");
    } finally {
      setOuranHistoryLoading(false);
    }
  };

  // Duas informações diferentes: a natureza (é o padrão ou é um ajuste?) e o
  // nível em que o ajuste vale. Uma tag só misturava as duas.
  // A timeline guarda só ajustes. Sem uma linha para a categoria do catálogo,
  // um SKU nunca ajustado abre a modal vazia e não dá para ver desde quando a
  // categoria atual vale. A montagem dessa linha vive em ouranSkuView, com
  // teste — foi ali que ela já mentiu uma vez.
  const ouranTimelineRows = useMemo(() => {
    const base = linhaBaseDaTimeline(
      ouranHistoryProduct?.ouran_pilot,
      ouranHistoryRows,
      catalogPilot?.ouran_mode_start
    );
    return base ? [...ouranHistoryRows, base] : ouranHistoryRows;
  }, [catalogPilot, ouranHistoryRows, ouranHistoryProduct]);

  // A busca é do servidor nos dois modos. Em memória ela só enxergaria a página
  // carregada. No modo Ouran, o cadastro resolve nome, EAN e código, mas o
  // resultado continua restrito aos SKUs vendidos no período.
  const filteredProducts = products;

  const pendingCount = Object.keys(pendingChanges).length;

  const columns = [
    {
      title: "SKU",
      dataIndex: "produto",
      width: 180,
      render: (val) => (
        <Text code style={{ whiteSpace: "nowrap", fontSize: 12 }}>
          {val}
        </Text>
      ),
    },
    {
      title: "Produto",
      dataIndex: "descricao",
      render: (descricao, record) => (
        <span>
          <span>{descricao || "—"}</span>
          {record.ean && (
            <Text style={{ color: "#8c8c8c", fontSize: 11, marginLeft: 6 }}>
              {record.ean}
            </Text>
          )}
        </span>
      ),
    },
    {
      title: "Receita no período",
      key: "pilot_revenue",
      hidden: !showOuranPilot,
      // Sem sorter local: o servidor já devolve tudo ranqueado por receita, e
      // ordenar no cliente reordenaria só os 50 da página atual.
      render: (_, record) => formatMoney(record.ouran_pilot?.receita),
    },
    {
      title: "Categoria ERP",
      key: "erp_category",
      hidden: !showOuranPilot,
      render: (_, record) => record.ouran_pilot?.categoria_erp || "—",
    },
    {
      // Efetiva e padrão eram duas colunas com o mesmo texto sempre que não havia
      // ajuste, que é o caso da maioria. Agora é uma só, e a tag diz a procedência;
      // o padrão só aparece quando difere da efetiva.
      title: "Categoria efetiva",
      key: "ouran_effective",
      hidden: !showOuranPilot,
      render: (_, record) => {
        const pilot = record.ouran_pilot;
        // categoria_padrao ja considera correcao de padrao; categoria_ouran e'
        // o valor cru do catalogo.
        const padrao = pilot?.categoria_padrao;
        const efetiva = pilot?.categoria_efetiva;
        return (
          <Space direction="vertical" size={2}>
            <span>{efetiva || "Sem categoria"}</span>
            <Space size={4} wrap>
              <Tag color={corDaOrigem(pilot)}>
                {rotuloDaOrigem(pilot?.categoria_efetiva_origem)}
              </Tag>
              {nivelDaOrigem(pilot?.categoria_efetiva_origem) && (
                <Tag>{nivelDaOrigem(pilot?.categoria_efetiva_origem)}</Tag>
              )}
            </Space>
            {padrao && padrao !== efetiva && (
              <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
                categoria padrão: {padrao}
              </span>
            )}
            {pilot?.correcao_ativa && <Tag color="gold">padrão corrigido</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Ajuste manual",
      key: "ouran_adjustment",
      hidden: !showOuranPilot,
      width: 170,
      render: (_, record) => (
        <Space wrap size={4}>
          {catalogPilot?.can_adjust_branch && (
            <Button size="small" onClick={() => openOuranAdjustment(record)}>
              Ajustar
            </Button>
          )}
          <Button
            type="text"
            icon={<HistoryOutlined />}
            size="small"
            aria-label={`Ver timeline Ouran do SKU ${record.produto}`}
            onClick={() => openOuranHistory(record)}
          />
        </Space>
      ),
    },
    {
      title: "Categoria manual",
      dataIndex: "col_sequencial",
      width: 260,
      hidden: showOuranPilot,
      render: (current, record) => {
        const value =
          record.produto in pendingChanges
            ? pendingChanges[record.produto]
            : current;
        const isDirty = record.produto in pendingChanges;

        return (
          <Select
            options={collectionOptions}
            value={value ?? null}
            onChange={(v) => handleCollectionChange(record.produto, v)}
            placeholder="Sem atribuição"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            style={{
              width: "100%",
              borderRadius: 4,
              outline: isDirty ? "1px solid #1677ff" : undefined,
            }}
          />
        );
      },
    },
    {
      title: "",
      key: "remove",
      width: 40,
      render: (_, record) =>
        !showOuranPilot && record.col_sequencial != null ? (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleRemove(record.produto)}
          />
        ) : null,
    },
    {
      title: "",
      key: "history",
      width: 40,
      render: (_, record) =>
        !showOuranPilot ? (
          <Button
            type="text"
            icon={<HistoryOutlined />}
            size="small"
            onClick={() => openHistoryModal(record.produto)}
          />
        ) : null,
    },
  ];

  return (
    <>
      {contextHolder}
      <Card
        title="Estrutura de categorias por SKU"
        style={{ width: "90%" }}
        extra={
          showOuranPilot ? null : (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!pendingCount}
              onClick={handleSave}>
              Salvar{pendingCount > 0 ? ` (${pendingCount})` : ""}
            </Button>
          )
        }>
        <Row gutter={[0, 8]}>
          <Col span={24}>
            <Text type="secondary">
              {showOuranPilot
                ? "Confira o padrão e ajuste a categoria efetiva. Os ajustes ficam na timeline do piloto e ainda não alteram relatórios."
                : "Produtos vendidos desde 01/05/2026. Revise as atribuições antes de salvar."}
            </Text>
          </Col>
          {!showOuranPilot && user?.is_superuser && catalogPilot && (
            <Col span={24}>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message="Filial em modo ERP"
                description="A categorização segue as coleções legadas. Ativar o modo Ouran troca a fonte para o catálogo global — não é um ato neutro, revise a prévia antes de confirmar."
                action={
                  <Button size="small" danger onClick={abrirAtivacaoOuran}>
                    Ativar modo Ouran
                  </Button>
                }
              />
            </Col>
          )}
          {!showOuranPilot && (
            <Col span={24}>
              <Collapse
                ghost
                style={{ marginBottom: 8 }}
                items={[
                  {
                    key: "manager",
                    label: (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Gerenciar categorias manuais
                      </Text>
                    ),
                    children: (
                      <>
                        <List
                          size="small"
                          dataSource={collectionOptions}
                          style={{ marginBottom: 8 }}
                          renderItem={(col) => (
                            <List.Item style={{ padding: "4px 0" }}>
                              <Text style={{ fontSize: 13 }}>{col.label}</Text>
                            </List.Item>
                          )}
                        />
                        <Row gutter={8}>
                          <Col flex="1">
                            <Input
                              placeholder="Nome da nova categoria"
                              value={newCollectionName}
                              onChange={(e) =>
                                setNewCollectionName(e.target.value)
                              }
                              onPressEnter={handleAddCollection}
                              size="small"
                            />
                          </Col>
                          <Col>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              loading={addingCollection}
                              disabled={!newCollectionName.trim()}
                              onClick={handleAddCollection}>
                              Adicionar
                            </Button>
                          </Col>
                        </Row>
                      </>
                    ),
                  },
                ]}
              />
            </Col>
          )}
          <Col span={24}>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col flex="1 1 240px">
                <Input.Search
                  placeholder="Buscar produto, SKU ou EAN..."
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>
              <Col flex="0 0 260px">
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  allowEmpty={[true, true]}
                  placeholder={["Receita desde", "até"]}
                  value={periodo}
                  onChange={setPeriodo}
                  disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
                />
              </Col>
              {showOuranPilot && (
                <>
                  <Col flex="0 0 240px">
                    <Select
                      placeholder="Categoria"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                      options={[
                        { value: "__none__", label: "— Sem categoria —" },
                        ...(catalogPilot?.categories || []).map((item) => ({
                          value: item.id,
                          label: item.name,
                        })),
                      ]}
                      value={filterCategoria}
                      onChange={(v) => setFilterCategoria(v ?? null)}
                    />
                  </Col>
                  <Col flex="0 0 240px">
                    <Select
                      placeholder="Categoria ERP"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                      options={erpFilterOptions}
                      value={filterCategoriaErp}
                      onChange={(v) => setFilterCategoriaErp(v ?? null)}
                    />
                  </Col>
                </>
              )}
              {!showOuranPilot && (
                <Col flex="0 0 260px">
                  <Select
                    placeholder="Filtrar por categoria"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: "100%" }}
                    options={[
                      { value: "__none__", label: "— Sem categoria —" },
                      ...collectionOptions,
                    ]}
                    value={filterCollection}
                    onChange={(v) => setFilterCollection(v ?? null)}
                  />
                </Col>
              )}
            </Row>
          </Col>
          {!showOuranPilot && selectedRowKeys.length > 0 && (
            <Col span={24}>
              <Row
                gutter={8}
                align="middle"
                style={{
                  marginBottom: 8,
                  padding: "6px 10px",
                  background: "#f0f5ff",
                  borderRadius: 6,
                  border: "1px solid #adc6ff",
                }}>
                <Col>
                  <Text strong style={{ fontSize: 13 }}>
                    {selectedRowKeys.length} selecionado(s)
                  </Text>
                </Col>
                <Col flex="0 0 240px">
                  <Select
                    placeholder="Atribuir categoria em lote"
                    options={collectionOptions}
                    value={bulkCollection}
                    onChange={setBulkCollection}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: "100%" }}
                    size="small"
                  />
                </Col>
                <Col>
                  <Button
                    type="primary"
                    size="small"
                    disabled={!bulkCollection}
                    onClick={handleBulkAssign}>
                    Aplicar
                  </Button>
                </Col>
                <Col>
                  <Button size="small" onClick={() => setSelectedRowKeys([])}>
                    Cancelar
                  </Button>
                </Col>
              </Row>
            </Col>
          )}
          <Col span={24}>
            {loading ? (
              <Spin />
            ) : (
              <Table
                dataSource={filteredProducts}
                columns={columns}
                rowKey="produto"
                size="small"
                scroll={showOuranPilot ? { x: 1450 } : undefined}
                pagination={{
                  // Paginação do servidor nos dois modos: a tabela só tem a
                  // página, então o total vem do backend. No modo Ouran isto
                  // era paginação local sobre os 50 da página 1 — sobra do
                  // tempo do lote — e a tela nunca passava da primeira página.
                  current: pagina,
                  pageSize: TAMANHO_PAGINA,
                  total: totalProdutos,
                  showSizeChanger: false,
                  showTotal: (t) => `${t} SKUs com venda no período`,
                  onChange: (p) => carregarPagina(p),
                }}
                rowSelection={
                  showOuranPilot
                    ? undefined
                    : { selectedRowKeys, onChange: setSelectedRowKeys }
                }
                rowClassName={(record) =>
                  record.produto in pendingChanges
                    ? "ant-table-row-selected"
                    : ""
                }
              />
            )}
          </Col>
        </Row>
      </Card>
      <Modal
        title="Criar categoria"
        open={categoryOpen}
        onCancel={() => !categorySaving && setCategoryOpen(false)}
        onOk={() => categoryForm.submit()}
        confirmLoading={categorySaving}
        okText="Criar">
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCreateCategory}
          initialValues={{ escopo: "branch" }}>
          <Form.Item
            name="name"
            label="Nome da categoria"
            rules={[{ required: true, message: "Informe o nome." }]}>
            <Input maxLength={100} placeholder="Ex.: Promoção Dia das Mães" />
          </Form.Item>
          <Form.Item name="escopo" label="Onde ela existe">
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="branch">
                  Somente nesta filial — para promoção, ponta de gôndola e
                  coisas locais
                </Radio>
                <Radio
                  value="client"
                  disabled={!catalogPilot?.can_adjust_client}>
                  Em todas as filiais deste cliente
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`Ajustar categoria Ouran — SKU ${ouranAdjustment?.produto || ""}`}
        open={Boolean(ouranAdjustment)}
        width={720}
        confirmLoading={ouranAdjustmentSaving}
        onCancel={() => {
          if (ouranAdjustmentSaving) return;
          setOuranAdjustment(null);
          setOuranAdjustmentPreview(null);
          setOuranAdjustmentPayload(null);
        }}
        footer={[
          <Button
            key="cancel"
            disabled={ouranAdjustmentSaving}
            onClick={() => {
              setOuranAdjustment(null);
              setOuranAdjustmentPreview(null);
              setOuranAdjustmentPayload(null);
            }}>
            Cancelar
          </Button>,
          ouranAdjustmentPreview ? (
            <Button
              key="confirm"
              type="primary"
              loading={ouranAdjustmentSaving}
              onClick={confirmOuranAdjustment}>
              Confirmar ajuste
            </Button>
          ) : (
            <Button
              key="preview"
              type="primary"
              loading={ouranAdjustmentSaving}
              onClick={prepareOuranAdjustment}>
              Revisar impacto
            </Button>
          ),
        ]}>
        <Typography.Paragraph type="secondary">
          {ouranAdjustment?.descricao}. A nova vigência encerra o ajuste ativo;
          ela nunca usa a categoria do ERP como fallback.
        </Typography.Paragraph>
        <Form
          form={adjustmentForm}
          layout="vertical"
          onValuesChange={(changed) => {
            setOuranAdjustmentPreview(null);
            setOuranAdjustmentPayload(null);
            // Trocar para escopo cliente invalida uma categoria de filial já
            // escolhida; limpa em vez de deixar um valor que será recusado.
            if (changed.scope === "client") {
              const atual = adjustmentForm.getFieldValue("category_id");
              const cat = (catalogPilot?.categories || []).find(
                (c) => c.id === atual
              );
              if (cat?.escopo === "branch") {
                adjustmentForm.setFieldsValue({ category_id: null });
              }
            }
          }}>
          <Form.Item
            name="intencao"
            label="O que você quer fazer"
            rules={[{ required: true }]}>
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="exposicao">
                  Vincular a uma categoria temporária
                  <div style={hintStyle}>
                    A categoria padrão continua valendo para agrupamento. Ex.:
                    promoção, ponta de gôndola.
                  </div>
                </Radio>
                <Radio value="correcao">
                  A categoria padrão está errada
                  <div style={hintStyle}>
                    Corrige a referência de agrupamento deste produto.
                  </div>
                </Radio>
                <Radio value="voltar_padrao" disabled={!temAjusteAtivo}>
                  Voltar à categoria padrão
                  <div style={hintStyle}>
                    {!temAjusteAtivo
                      ? "Este SKU já está na categoria padrão."
                      : vinculoEstaDesviando(ouranAdjustment?.ouran_pilot)
                        ? "Encerra a categoria temporária deste SKU."
                        : "Desfaz a correção do padrão; volta ao que o catálogo diz."}
                  </div>
                </Radio>
                <Radio value="sem_categoria">Deixar sem categoria</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="scope"
            label="Onde aplicar"
            rules={[{ required: true }]}>
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="branch">Somente esta filial e este SKU</Radio>
                <Radio value="client" disabled={!escopoCliente.habilitado}>
                  Todas as filiais deste cliente com o mesmo EAN
                  {escopoCliente.motivo && (
                    <div style={hintStyle}>{escopoCliente.motivo}</div>
                  )}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {precisaCategoria && (
            <Form.Item
              name="category_id"
              label="Categoria"
              rules={[{ required: true, message: "Selecione uma categoria." }]}>
              <Select
                showSearch
                optionFilterProp="label"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div style={{ padding: 8, borderTop: "1px solid #f0f0f0" }}>
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          // Nasce no mesmo escopo do ajuste: categoria de
                          // filial é recusada em ajuste de cliente, e descobrir
                          // isso só no erro seria um beco.
                          categoryForm.setFieldsValue({
                            escopo:
                              adjustmentForm.getFieldValue("scope") === "client"
                                ? "client"
                                : "branch",
                          });
                          setCategoryOpen(true);
                        }}>
                        Criar nova categoria
                      </Button>
                    </div>
                  </>
                )}
                options={categoriasDisponiveis.map((category) => ({
                  value: category.id,
                  label:
                    category.escopo === "global"
                      ? category.name
                      : `${category.name} (${
                          category.escopo === "branch" ? "filial" : "cliente"
                        })`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="start_date"
            label="Início da vigência"
            rules={[{ required: true }]}>
            <DatePicker
              format="DD/MM/YYYY"
              disabledDate={(date) => date && date.isAfter(dayjs(), "day")}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Motivo"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Informe o motivo.",
              },
            ]}>
            <Input.TextArea
              maxLength={1000}
              showCount
              placeholder="Explique por que este SKU deve seguir essa categoria."
            />
          </Form.Item>
        </Form>
        {ouranAdjustmentPreview && (
          <Alert
            type="warning"
            showIcon
            message={resumoImpactoOuran(ouranAdjustmentPreview)}
            description={
              <>
                {ouranAdjustmentPreview.branches?.length > 0 && (
                  <List
                    header={<Text strong>Filiais em modo Ouran</Text>}
                    size="small"
                    dataSource={ouranAdjustmentPreview.branches}
                    renderItem={(branch) => (
                      <List.Item>
                        <Text>
                          {branch.branch_name}: SKU(s) {branch.skus.join(", ")}{" "}
                          — {formatMoney(branch.revenue)} no período
                        </Text>
                      </List.Item>
                    )}
                  />
                )}
                {ouranAdjustmentPreview.dormant_branches?.length > 0 && (
                  <List
                    header={
                      <Text strong>
                        Filiais legadas — ajuste preparado, ainda sem efeito
                      </Text>
                    }
                    size="small"
                    dataSource={ouranAdjustmentPreview.dormant_branches}
                    renderItem={(branch) => (
                      <List.Item>
                        <Text type="secondary">
                          {branch.branch_name}: SKU(s) {branch.skus.join(", ")}{" "}
                          — {formatMoney(branch.revenue)} no período
                        </Text>
                      </List.Item>
                    )}
                  />
                )}
                <Text type="secondary">
                  O ajuste passa a valer operacionalmente quando a filial está
                  em modo Ouran.
                </Text>
              </>
            }
          />
        )}
      </Modal>
      <Modal
        open={Boolean(ouranHistoryProduct)}
        onCancel={() => setOuranHistoryProduct(null)}
        title={`Timeline Ouran — SKU ${ouranHistoryProduct?.produto || ""}`}
        footer={null}
        width={920}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Para substituir um ajuste, registre uma nova vigência."
          description="Escolha outra categoria, volte à categoria padrão ou deixe o SKU sem categoria."
        />
        <Table
          loading={ouranHistoryLoading}
          dataSource={ouranTimelineRows}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{
            emptyText: "Nenhum ajuste registrado para este SKU ou EAN.",
          }}
          columns={[
            {
              title: "Escopo",
              dataIndex: "scope",
              render: (scope, row) =>
                row.baseline
                  ? "Catálogo Ouran"
                  : scope === "client"
                    ? "Todo o cliente (EAN)"
                    : "Filial (SKU)",
            },
            {
              // Sem isto, "foi pra ponta de gôndola" e "a classificação estava
              // errada" apareciam idênticas na timeline.
              title: "Natureza",
              key: "tipo",
              render: (_, row) =>
                row.baseline ? (
                  <Tag>Catálogo</Tag>
                ) : row.tipo === "correcao_padrao" ? (
                  <Tag color="gold">Correção do padrão</Tag>
                ) : (
                  <Tag color="purple">Temporário</Tag>
                ),
            },
            {
              title: "Categoria",
              key: "resolution",
              render: (_, row) =>
                row.baseline
                  ? row.category_name
                  : row.resolution === "category"
                    ? row.category_name
                    : row.resolution === "standard"
                      ? "Categoria padrão"
                      : "Sem categoria",
            },
            {
              title: "Início",
              dataIndex: "start",
              // A linha base nao tem inicio: o padrao vale desde sempre, ate o
              // primeiro ajuste comecar.
              render: (value) =>
                value ? dayjs(value).format("DD/MM/YYYY") : "—",
            },
            {
              title: "Fim",
              dataIndex: "end",
              // Dois ajustes abertos podem coexistir (filial abrindo exceção a
              // um ajuste do cliente). Marcar os dois como "Ativo" escondia
              // qual deles o relatório usaria nesta filial.
              render: (value, row) =>
                value ? (
                  dayjs(value).format("DD/MM/YYYY")
                ) : row.vigente === false ? (
                  <Tooltip title="Um ajuste de filial mais específico está valendo aqui. Este segue valendo nas demais filiais do cliente.">
                    <Tag color="orange">Sobreposto</Tag>
                  </Tooltip>
                ) : (
                  <Tag color="green">Vigente</Tag>
                ),
            },
            { title: "Autor", dataIndex: "created_by" },
            { title: "Motivo", dataIndex: "reason" },
            {
              title: "",
              key: "acoes",
              // Só o segmento aberto é editável: histórico fechado é registro do
              // que valeu e não se reescreve.
              render: (_, row) =>
                !row.baseline && !row.end && catalogPilot?.can_adjust_branch ? (
                  <Space size={4}>
                    <Button size="small" onClick={() => abrirEdicao(row)}>
                      Editar
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={excluindo === row.id}
                      onClick={() => confirmarExclusao(row)}
                    />
                  </Space>
                ) : null,
            },
          ]}
        />
      </Modal>
      <Modal
        title="Editar ajuste vigente"
        open={Boolean(edicao)}
        onCancel={() => !edicaoSaving && setEdicao(null)}
        onOk={() => edicaoForm.submit()}
        confirmLoading={edicaoSaving}
        okText="Salvar">
        <Form form={edicaoForm} layout="vertical" onFinish={salvarEdicao}>
          <Form.Item
            name="resolution"
            label="O que fazer"
            rules={[{ required: true }]}>
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="category">Vincular a uma categoria</Radio>
                <Radio value="standard">Aplicar a categoria padrão</Radio>
                <Radio value="unclassified">Deixar sem categoria</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {edicaoResolution === "category" && (
            <Form.Item
              name="category_id"
              label="Categoria"
              rules={[{ required: true, message: "Selecione uma categoria." }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(catalogPilot?.categories || [])
                  .filter(
                    (c) => edicao?.scope !== "client" || c.escopo !== "branch"
                  )
                  .map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="start_date"
            label="Início da vigência"
            rules={[{ required: true }]}>
            <DatePicker
              format="DD/MM/YYYY"
              disabledDate={(date) => date && date.isAfter(dayjs(), "day")}
            />
          </Form.Item>
          <Form.Item name="reason" label="Motivo" rules={[{ required: true }]}>
            <Input.TextArea maxLength={1000} rows={3} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={historyModalProduto != null}
        onCancel={() => {
          historyModalProdutoRef.current = null;
          setHistoryModalProduto(null);
        }}
        title={`Histórico de atribuição — SKU ${historyModalProduto ?? ""}`}
        footer={null}
        width={640}>
        <Table
          loading={historyLoading}
          dataSource={historyRows}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            {
              title: "Categoria",
              dataIndex: "col_sequencial",
              render: (col) =>
                collectionOptions.find((c) => c.value === col)?.label ?? col,
            },
            {
              title: "Início",
              dataIndex: "union_start_datetime",
              sorter: (a, b) =>
                new Date(a.union_start_datetime) -
                new Date(b.union_start_datetime),
              render: (text) => new Date(text).toLocaleDateString("pt-BR"),
            },
            {
              title: "Fim",
              dataIndex: "union_end_datetime",
              sorter: (a, b) => {
                const aEnd = a.union_end_datetime
                  ? new Date(a.union_end_datetime).getTime()
                  : Infinity;
                const bEnd = b.union_end_datetime
                  ? new Date(b.union_end_datetime).getTime()
                  : Infinity;
                return aEnd - bEnd;
              },
              render: (text) =>
                text ? new Date(text).toLocaleDateString("pt-BR") : "Ativo",
            },
            {
              title: "Ações",
              key: "actions",
              render: (_, record) => (
                <TimelineSegmentActions
                  record={record}
                  idField="id"
                  entityLabel="atribuição"
                  onEdit={(id, params) => patchSkuCollectionSegment(id, params)}
                  onDelete={(id, reason) =>
                    deleteSkuCollectionSegment(id, reason)
                  }
                  onDone={() => refreshHistoryAndGridRow(historyModalProduto)}
                  messageApi={messageApi}
                />
              ),
            },
          ]}
        />
      </Modal>
      <Modal
        open={activateOpen}
        onCancel={() => setActivateOpen(false)}
        title="Ativar modo Ouran"
        okText="Ativar"
        okButtonProps={{ danger: true, loading: activating }}
        confirmLoading={activating}
        onOk={() => activateForm.submit()}>
        {activatePreviewLoading ? (
          <Spin />
        ) : (
          activatePreview && (
            <Form
              form={activateForm}
              layout="vertical"
              onFinish={confirmarAtivacaoOuran}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Filial ${activatePreview.category_mode} → ${activatePreview.modo_alvo}`}
                description="O histórico anterior é preservado; relatórios já fechados não mudam. Rollback existe e foi testado."
              />
              {activatePreview.ajustes_cliente_ativados > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`${activatePreview.ajustes_cliente_ativados} ajuste(s) de escopo cliente já cadastrado(s) passam a valer nesta filial imediatamente.`}
                  description="Foram criados em algum momento (nesta filial ou noutra do mesmo cliente) e respeitam vigência — não são residuo automático, mas confira se ainda fazem sentido antes de confirmar."
                />
              )}
              <Form.Item
                name="release_id"
                label="Release do catálogo"
                rules={[{ required: true, message: "Selecione uma release" }]}>
                <Select
                  options={(activatePreview.releases || []).map((r) => ({
                    value: r.id,
                    label: r.version,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="reason"
                label="Motivo"
                rules={[{ required: true, message: "Descreva o motivo" }]}>
                <Input.TextArea maxLength={1000} rows={3} />
              </Form.Item>
            </Form>
          )
        )}
      </Modal>
    </>
  );
}
